import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Sparkles } from 'lucide-react';
import { ConversationalInput } from '@/components/shared/ConversationalInput';
import { QuickPrompts } from '@/components/shared/QuickPrompts';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface BigSheetSection {
  id: string;
  title: string;
  description: string;
  prompt: string;
  placeholder: string;
  estimatedMinutes: number;
  content: string;
  completed: boolean;
}

const sections: BigSheetSection[] = [
  {
    id: 'support-system',
    title: 'Your Support Network',
    description: 'Identify the people and communities that accelerate your career growth.',
    prompt: 'Who are the key people in your professional support system?',
    placeholder: 'List mentors, advocates, communities, or key relationships...',
    estimatedMinutes: 2,
    content: '',
    completed: false
  },
  {
    id: 'skills-inventory',
    title: 'Skills & Strengths',
    description: 'Map your current abilities and identify growth areas.',
    prompt: 'What are your strongest professional skills and biggest growth opportunities?',
    placeholder: 'List your key strengths and areas for development...',
    estimatedMinutes: 2,
    content: '',
    completed: false
  },
  {
    id: 'career-vision',
    title: 'Career Vision',
    description: 'Define where you want your career to go.',
    prompt: 'What does career success look like for you in the next 2-3 years?',
    placeholder: 'Describe your ideal career direction and goals...',
    estimatedMinutes: 2,
    content: '',
    completed: false
  },
  {
    id: 'achievements',
    title: 'Key Achievements',
    description: 'Document your most significant professional wins.',
    prompt: 'What are your biggest career achievements and their impact?',
    placeholder: 'List major accomplishments and their business impact...',
    estimatedMinutes: 2,
    content: '',
    completed: false
  },
  {
    id: 'values',
    title: 'Core Values',
    description: 'Identify what matters most in your work.',
    prompt: 'What values drive your career decisions and work preferences?',
    placeholder: 'List your core professional values and motivations...',
    estimatedMinutes: 1,
    content: '',
    completed: false
  }
];

export const ConversationalBigSheet: React.FC = () => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionsData, setSectionsData] = useState(sections);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!startTime) {
      setStartTime(new Date());
    }
  }, []);

  const currentSection = sectionsData[currentSectionIndex];
  const completedSections = sectionsData.filter(s => s.completed).length;
  const progress = (completedSections / sectionsData.length) * 100;
  const totalEstimatedTime = sectionsData.reduce((sum, s) => sum + s.estimatedMinutes, 0);
  const timeSpent = startTime ? Math.round((new Date().getTime() - startTime.getTime()) / 60000) : 0;

  const handleContentChange = (content: string) => {
    const updatedSections = [...sectionsData];
    updatedSections[currentSectionIndex] = {
      ...currentSection,
      content,
      completed: content.trim().length > 20 // Mark completed if substantial content
    };
    setSectionsData(updatedSections);
  };

  const handleNext = () => {
    if (currentSection.content.trim()) {
      if (currentSectionIndex < sectionsData.length - 1) {
        setCurrentSectionIndex(currentSectionIndex + 1);
      } else {
        // Journey complete
        toast({
          title: "Big Sheet™ Complete! 🎉",
          description: `You've completed your career archaeology in ${timeSpent} minutes.`,
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    // Generate AI suggestions based on the quick prompt
    generateCustomSuggestions(prompt);
  };

  const generateCustomSuggestions = async (promptText: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('artifact-ai-assistant', {
        body: {
          prompt: `User selected: "${promptText}". Provide 3-4 specific, editable examples for ${currentSection.title}.`,
          artifactType: 'big-sheet',
          sectionType: currentSection.id,
          userContext: `Quick prompt: ${promptText}`
        }
      });

      if (error) throw error;
      
      if (data?.suggestions) {
        // For now, just trigger a re-generation of suggestions in the ConversationalInput
        // The ConversationalInput component will pick up the new context
      }
    } catch (error) {
      console.error('Error generating custom suggestions:', error);
    }
  };

  const isLastSection = currentSectionIndex === sectionsData.length - 1;
  const canProceed = currentSection.content.trim().length > 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header with Progress */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Big Sheet™ Journey
            </h1>
            <p className="text-muted-foreground">Your guided career archaeology experience</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">
              {completedSections} of {sectionsData.length} sections
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{timeSpent}m / ~{totalEstimatedTime}m</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Overall Progress</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Section Navigation */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {sectionsData.map((section, index) => (
            <Button
              key={section.id}
              variant={index === currentSectionIndex ? "default" : section.completed ? "secondary" : "outline"}
              size="sm"
              onClick={() => setCurrentSectionIndex(index)}
              className="whitespace-nowrap flex items-center gap-2"
            >
              {section.completed && <CheckCircle className="h-3 w-3" />}
              <span className="text-xs">{section.title}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Current Section */}
      <div className="space-y-6">
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{currentSection.title}</h2>
                <p className="text-muted-foreground">{currentSection.description}</p>
              </div>
              <Badge variant="secondary" className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                ~{currentSection.estimatedMinutes}m
              </Badge>
            </div>
          </div>
        </Card>

        {/* Quick Prompts */}
        <QuickPrompts
          artifactType="big-sheet"
          sectionType={currentSection.id}
          onPromptSelect={handleQuickPrompt}
        />

        {/* Conversational Input */}
        <ConversationalInput
          question={currentSection.prompt}
          placeholder={currentSection.placeholder}
          value={currentSection.content}
          onChange={handleContentChange}
          artifactType="big-sheet"
          sectionType={currentSection.id}
          userContext={`Section: ${currentSection.title}. Previous sections completed: ${completedSections}`}
        />

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentSectionIndex === 0}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4" />
            <span>Section {currentSectionIndex + 1} of {sectionsData.length}</span>
          </div>

          <Button
            onClick={handleNext}
            disabled={!canProceed}
            className="flex items-center gap-2"
          >
            {isLastSection ? 'Complete Journey' : 'Next Section'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {!canProceed && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Add some content above to continue to the next section
            </p>
          </div>
        )}
      </div>
    </div>
  );
};